import { problemWorkApi } from '../problem-work/problem-work-api'
import { notificationsApi } from '../notifications/notifications-api'
import { createListenerMiddleware, isAnyOf, type UnknownAction } from '@reduxjs/toolkit'
import type { ThunkDispatch } from '@reduxjs/toolkit'

import { applyBranchSwitch } from '../../active-branch'
import { trackUsageAction } from '../../telemetry/usage-tracker'
import { notificationRulesApi } from '../notification-rules/notification-rules-api'
import { customAgentsApi } from '../agents/custom-agents/custom-agents-api'
import { agentWritesApi } from '../agents/agent-writes/agent-writes-api'
import { isMainBranch } from '../branches/branch-utils'
import { branchesApi } from '../branches/branches-api'
import {
  claimMergeWorkTerminal,
  type MergeWorkTerminalFields
} from '../branches/merge-work-terminal'
import { isMergeWorkActive } from '../branches/types'
import { getOntologyBranchHeaderValue } from '../core'
import { graphApi } from '../graph/graph-api'
import { ingestApi } from '../ingest/ingest-api'
import { invalidateGraphData, invalidatePlantViewerData } from './graph-data-invalidation'
import { invalidateBranchIngestPaint } from './branch-ingest-paint-invalidation'
import { nextDomainTopic } from './domain-event-bus'

/** Bounds the wait for a merge wake-up the stream missed; not a polling cadence. */
export const MERGE_WAKE_FALLBACK_MS = 30_000

const graphApiWrites = isAnyOf(
  graphApi.endpoints.runCypherWrite.matchFulfilled,
  graphApi.endpoints.updateManualNode.matchFulfilled,
  graphApi.endpoints.deleteManualNode.matchFulfilled,
  graphApi.endpoints.recordNodeEvidence.matchFulfilled,
  graphApi.endpoints.clearNeo4j.matchFulfilled
)

const unconditionalExternalGraphWrites = isAnyOf(
  ingestApi.endpoints.applyStructuredDiff.matchFulfilled,
  branchesApi.endpoints.restoreBranch.matchFulfilled
)

export function shouldInvalidatePlantViewerData(action: UnknownAction): boolean {
  return (
    graphApiWrites(action) ||
    customAgentsApi.endpoints.updateCustomAgentTriggerOccurrence.matchFulfilled(action) ||
    notificationRulesApi.endpoints.updateNotificationRuleTriggerOccurrence.matchFulfilled(action)
  )
}

export function shouldInvalidateGraphData(action: UnknownAction): boolean {
  if (unconditionalExternalGraphWrites(action)) return true

  if (agentWritesApi.endpoints.applyCypherWrite.matchFulfilled(action)) {
    return action.payload.ok && action.meta.arg.originalArgs.graphAffecting
  }
  if (branchesApi.endpoints.revertCommit.matchFulfilled(action)) {
    return action.payload.status === 'reverted'
  }
  if (branchesApi.endpoints.mergeBranch.matchFulfilled(action)) {
    return action.payload.status === 'merged'
  }
  // mergeMergeRequest fulfillment is acceptance (202 queued), not a landed merge.
  // Terminal apply success is handled by the merge-work observer below.

  return false
}

function mergeRequestsFromAction(action: UnknownAction): MergeWorkTerminalFields[] {
  if (branchesApi.endpoints.listMergeRequests.matchFulfilled(action)) {
    return action.payload
  }
  if (branchesApi.endpoints.getMergeRequest.matchFulfilled(action)) {
    return [action.payload]
  }
  return []
}

/**
 * After apply lands, leave a consumed source branch for the merge target.
 * Best-effort and page-agnostic: only runs when the active branch is the
 * merged-away source. Main targets clear the header (baseline); others open
 * the target first so the switch never lands on a missing working DB.
 */
export async function switchOffMergedSource(
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
  mr: MergeWorkTerminalFields
): Promise<void> {
  const activeId = getOntologyBranchHeaderValue()
  if (!activeId || !mr.sourceId || activeId !== mr.sourceId || !mr.targetId) return
  try {
    const target = await dispatch(branchesApi.endpoints.getBranch.initiate(mr.targetId)).unwrap()
    if (isMainBranch(target)) {
      applyBranchSwitch(dispatch, undefined)
      return
    }
    await dispatch(branchesApi.endpoints.openBranch.initiate(target.id)).unwrap()
    applyBranchSwitch(dispatch, target.id)
  } catch {
    // Stale-branch recovery on the next Branches list may still reset the header.
  }
}

export const cacheCoherenceBridge = createListenerMiddleware()

cacheCoherenceBridge.startListening({
  predicate: shouldInvalidatePlantViewerData,
  effect: (_action, api) => {
    invalidatePlantViewerData(api.dispatch)
  }
})

cacheCoherenceBridge.startListening({
  predicate: shouldInvalidateGraphData,
  effect: (action, api) => {
    invalidateGraphData(api.dispatch)
    if (agentWritesApi.endpoints.applyCypherWrite.matchFulfilled(action)) {
      const branchId = action.meta.arg.originalArgs.branchId
      invalidateBranchIngestPaint(api.dispatch, branchId)
    }
  }
})

/**
 * Terminal merge-work observer: list/detail fetches discover preview/apply
 * completion. Deduped so repeated fetches do not re-invalidate or re-emit
 * analytics. Preview success may have auto-committed dirty sides (Branches +
 * Commits only). Apply success also refreshes graph/plant, emits
 * branch_merged, and switches off a consumed source branch.
 */
cacheCoherenceBridge.startListening({
  matcher: isAnyOf(
    branchesApi.endpoints.listMergeRequests.matchFulfilled,
    branchesApi.endpoints.getMergeRequest.matchFulfilled
  ),
  effect: (action, api) => {
    for (const mr of mergeRequestsFromAction(action)) {
      const kind = claimMergeWorkTerminal(mr)
      if (!kind) continue
      api.dispatch(branchesApi.util.invalidateTags(['Branches', 'Commits']))
      if (kind === 'apply_succeeded') {
        invalidateGraphData(api.dispatch)
        trackUsageAction('branches', 'branch_merged')
        void switchOffMergedSource(api.dispatch, mr)
      }
    }
  }
})

/**
 * Keep refetching an apply this tab enqueued until it terminals — on each merge
 * wake-up, or after the fallback wait without one — so completion side effects
 * (cache, analytics, branch switch) still fire if the user leaves Merge Review /
 * Merges before the worker finishes.
 */
cacheCoherenceBridge.startListening({
  matcher: branchesApi.endpoints.mergeMergeRequest.matchFulfilled,
  effect: async (action, api) => {
    const id = action.payload.id
    while (!api.signal.aborted) {
      await nextDomainTopic('merges', MERGE_WAKE_FALLBACK_MS, api.signal)
      if (api.signal.aborted) return
      // Unsubscribed each pass so merge wake-ups don't keep refetching it afterwards.
      const request = api.dispatch(
        branchesApi.endpoints.getMergeRequest.initiate(id, { forceRefetch: true })
      )
      try {
        const mr = await request.unwrap()
        if (mr.status === 'merged' || mr.status === 'closed') return
        if (mr.workKind === 'apply' && mr.workStatus === 'failed') return
        if (!isMergeWorkActive(mr.workStatus)) return
      } catch {
        return
      } finally {
        request.unsubscribe()
      }
    }
  }
})

cacheCoherenceBridge.startListening({
  matcher: notificationsApi.endpoints.closeNotificationOccurrence.matchFulfilled,
  effect: (_action, api) => {
    api.dispatch(problemWorkApi.util.invalidateTags(['Work']))
  }
})
cacheCoherenceBridge.startListening({
  matcher: isAnyOf(
    problemWorkApi.endpoints.setProblemTasksComplete.matchFulfilled,
    problemWorkApi.endpoints.setProblemOccurrenceStatus.matchFulfilled
  ),
  effect: (_action, api) => {
    api.dispatch(notificationsApi.util.invalidateTags(['Notification', 'NotificationCounts']))
  }
})
